<script setup lang="ts">
import { computed, ref, watch, useTemplateRef } from 'vue';
import { useScroll } from '@vueuse/core';

import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { AppMaskType } from '@libs/ui';
import { AppButton, AppMask } from '@libs/ui';

import type { HomeFlightData } from '../../../../modules/airportSelector';
import { HomeAirportSelectorButton } from '../../../../modules/airportSelector';
import { PaxSelectorButton } from '../../../../modules/paxSelector';

interface TripOverviewHeaderActionsDesktopProps {
  paxesButtonText: string;
  homeFlightData: HomeFlightData | null;
  tripStartDate: Date | undefined;
  numberOfDestinations: number;
  priceCalendarButtonText: string;
  numberOfNights: number;
  firstDestinationName?: string;
  isUserSelectedItinerary?: boolean;
}

const props = defineProps<TripOverviewHeaderActionsDesktopProps>();

interface TripOverviewHeaderActionsDesktopEmits {
  (eventName: 'pax-click'): void;
  (eventName: 'edit-trip'): void;
  (eventName: 'home-airport-click'): void;
  (eventName: 'months-selector-click'): void;
}

const emit = defineEmits<TripOverviewHeaderActionsDesktopEmits>();

const statsigEventsStore = useStatsigEventsStore();

const actionsRef = useTemplateRef('actionsRef');
const { t } = i18n.global;

const { arrivedState } = useScroll(actionsRef, {
  behavior: 'smooth',
});

const maskType = computed<AppMaskType>(() => {
  if (arrivedState.left && arrivedState.right) {
    return 'none';
  }
  if (arrivedState.left) {
    return 'right';
  }
  if (arrivedState.right) {
    return 'left';
  }
  return 'both';
});

const monthLabel = computed<string>(() =>
  props.isUserSelectedItinerary
    ? props.priceCalendarButtonText
    : `${t('planner.flexible_in')} ${props.priceCalendarButtonText}`
);

const firstDestinationNameSaved = ref<string | undefined>(undefined);

watch(
  () => props.firstDestinationName,
  (newValue) => {
    if (newValue && newValue !== firstDestinationNameSaved.value) {
      firstDestinationNameSaved.value = newValue;
    }
  },
  { immediate: true }
);

const firstDestinationNameDisplay = computed<string>(() => {
  if (props.numberOfDestinations === 1 && firstDestinationNameSaved.value) {
    return firstDestinationNameSaved.value;
  }

  return `${props.numberOfDestinations} ${t(
    'planner.destination',
    props.numberOfDestinations
  ).toLowerCase()}`;
});

function openEditTripModal(actionType: 'editTrip' | 'editStay'): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType,
  });

  emit('edit-trip');
}

function openHomeAirportModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'homeAirport',
  });

  emit('home-airport-click');
}

function openPaxSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'paxSelector',
  });

  emit('pax-click');
}

function openMonthsSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'monthsSelector',
  });

  emit('months-selector-click');
}
</script>

<template>
  <AppMask :type="maskType">
    <div ref="actionsRef" class="trip-overview-header-actions-desktop">
      <HomeAirportSelectorButton
        v-if="props.homeFlightData"
        :home-flight-data="props.homeFlightData"
        space-between
        type="modern-light"
        size="medium"
        @click="openHomeAirportModal"
      />

      <AppButton
        class="edit-trip-button"
        type="modern-light"
        state="enabled"
        size="medium"
        :left-icon="{
          size: '18',
          name: 'locationMarkOutlined',
        }"
        :right-icon="{
          size: '18',
          name: 'chevronDown',
        }"
        space-between
        @click="openEditTripModal('editTrip')"
      >
        {{ firstDestinationNameDisplay }}
      </AppButton>

      <PaxSelectorButton
        :button-text="props.paxesButtonText"
        space-between
        type="modern-light"
        size="medium"
        @click="openPaxSelectorModal"
      />

      <AppButton
        class="months-selector-modal-button"
        type="modern-light"
        state="enabled"
        size="medium"
        :left-icon="{
          size: '18',
          name: 'calendarOutlined',
        }"
        :right-icon="{
          size: '18',
          name: 'chevronDown',
        }"
        space-between
        @click="openMonthsSelectorModal()"
      >
        {{ monthLabel }}
      </AppButton>

      <AppButton
        class="edit-trip-button"
        type="modern-light"
        state="enabled"
        size="medium"
        :left-icon="{
          size: '18',
          name: 'moon',
        }"
        :right-icon="{
          size: '18',
          name: 'chevronDown',
        }"
        space-between
        @click="openEditTripModal('editStay')"
      >
        {{ props.numberOfNights }}
        {{ $t('planner.nights', props.numberOfNights).toLowerCase() }}
      </AppButton>
    </div>
  </AppMask>
</template>

<style scoped lang="scss">
.trip-overview-header-actions-desktop {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);
}
</style>
